// DS8.2.3
import React, { useEffect, useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { ReferralType, BiEventType } from '@wix/da-bi/pkg/constants';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import DisclosureBanner from '@wix/da-ds/pkg/banners/DisclosureBanner';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import CollectionsList from '../../../CollectionPanel/CollectionList';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DSModal, { MODAL_FOOTER_BUTTON_SIZE } from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import DSPopover, { POPOVER_FOOTER_BUTTON_SIZE } from '@wix/da-ds/pkg/popovers/dsPresets/Popover';
import NewCollectionPopup from './NewCollectionPopup';
import { getDeviationResourceType } from '../../../DeviationViews/_helpers';
import { getCurrentUserHasCore } from '../../../publicSession/selectors';
import BuyCoreButton from '../../../Button/BuyCoreButton/BuyCoreButton';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import s from './AddToCollectionPopup.scss';
const MAX_ALLOWED_PRIVATE_DEVIATIONS_FOR_NON_CORE = 10;
const AddToCollectionPopup = ({ isPrivateCollections, totalCountOfPrivatelyCollectedDeviations = 0, collectibleDeviation, collectibleDeviation: { deviationId }, className, isInitialized, hasMoreCollections, collectionsCount, ignorePreselectedCollections, notificationQueue, selectedFolderIds, initializeCollectionsForDeviation, commitCollections, removeQueues, onClose })=>{
    const { t } = useTranslation();
    const hasCoreMembership = useSelector(getCurrentUserHasCore);
    const reachedCollectLimit = isPrivateCollections && !hasCoreMembership && totalCountOfPrivatelyCollectedDeviations >= MAX_ALLOWED_PRIVATE_DEVIATIONS_FOR_NON_CORE;
    useEffect(()=>{
        if (!isInitialized) {
            initializeCollectionsForDeviation(deviationId, isPrivateCollections, get(collectibleDeviation, 'stats.favourites', 0), ignorePreselectedCollections);
        }
    }, [
        isInitialized,
        initializeCollectionsForDeviation,
        deviationId,
        collectibleDeviation,
        ignorePreselectedCollections,
        isPrivateCollections
    ]);
    // flush everything from a given (via props) notificationQueue on init
    // so that on save the new notification doesn't overlap with any old ones
    useEffect(()=>{
        removeQueues((name)=>name === notificationQueue);
    }, [
        notificationQueue,
        removeQueues
    ]);
    const isMobile = useContext(MobileContext);
    const [filterValue, setFilterValue] = useState('');
    const [showingNewCollectionPopup, setShowNewCollectionPopup] = useState(false);
    const onFilterInputChange = (e)=>setFilterValue(e.target.value);
    const onDone = ()=>{
        yieldToMain(()=>{
            commitCollections(collectibleDeviation, notificationQueue || 'default', isPrivateCollections);
            onClose();
        });
    };
    if (showingNewCollectionPopup) {
        return /*#__PURE__*/ React.createElement(NewCollectionPopup, {
            className: className,
            collectibleDeviation: collectibleDeviation,
            isPrivateCollections: isPrivateCollections,
            onClose: ()=>setShowNewCollectionPopup(false)
        });
    }
    const { author } = collectibleDeviation;
    const filterInput = !hasMoreCollections && collectionsCount >= 5 ? /*#__PURE__*/ React.createElement(TextInput, {
        id: "search-input",
        className: s['filter-input'],
        biData: BiData({
            evid: 2,
            click_info: 'addToCollectionPopover.filterInput',
            sectionname: BiContextProvidedValue,
            widgetname: BiContextProvidedValue,
            typeid: BiContextProvidedValue,
            itemid: BiContextProvidedValue
        }),
        label: t('addToCollectionPopover.input.filter.placeholder'),
        placeholder: t('addToCollectionPopover.input.filter.placeholder'),
        value: filterValue,
        onChange: onFilterInputChange,
        prefix: /*#__PURE__*/ React.createElement(SearchIcon, {
            disableDefaultColors: true
        }),
        noBottomSpacing: true,
        isLabelHidden: true
    }) : undefined;
    const footerPrimaryButton = reachedCollectLimit ? /*#__PURE__*/ React.createElement(BuyCoreButton, {
        size: "medium"
    }, t('common.upgradeCoreCTA')) : /*#__PURE__*/ React.createElement(SolidButton, {
        variant: "approval-green",
        biData: {
            evid: isPrivateCollections ? BiEventType.ADD_TO_PRIVATE_COLLECTION_DONE_CLICK : BiEventType.ADD_TO_COLLECTION_DONE_CLICK,
            creatorid: author && author.useridUuid,
            typeid: getDeviationResourceType(collectibleDeviation),
            itemid: deviationId,
            sectionname: undefined,
            collectionid: selectedFolderIds.length > 1 ? undefined : selectedFolderIds[0],
            num_collections: selectedFolderIds.length
        },
        onClick: onDone,
        size: isMobile ? MODAL_FOOTER_BUTTON_SIZE : POPOVER_FOOTER_BUTTON_SIZE
    }, t('common.done'));
    const newCollectionButton = /*#__PURE__*/ React.createElement(TextButton, {
        onClick: ()=>setShowNewCollectionPopup(true),
        icon: AddIcon,
        size: "small",
        variant: "main",
        disabled: isPrivateCollections && !hasCoreMembership,
        biData: BiData({
            evid: 2,
            click_info: isPrivateCollections ? 'addToCollectionPopover.newPrivateCollection' : 'addToCollectionPopover.newCollection',
            sectionname: BiContextProvidedValue,
            widgetname: BiContextProvidedValue,
            typeid: BiContextProvidedValue,
            itemid: BiContextProvidedValue
        })
    }, t('addToCollectionPopover.button.newCollection'));
    const popupTitle = t(isPrivateCollections ? 'addToCollectionPopover.private.title' : 'addToCollectionPopover.title');
    const popupSubtitle = isPrivateCollections ? t('addToCollectionPopover.private.subtitle') : undefined;
    const popupContent = /*#__PURE__*/ React.createElement(React.Fragment, null, !isPrivateCollections && filterInput, isPrivateCollections && !hasCoreMembership && /*#__PURE__*/ React.createElement(DisclosureBanner, {
        className: s['core-banner'],
        message: t(reachedCollectLimit ? 'addToCollectionPopover.coreBanner.reachedLimit.message' : 'addToCollectionPopover.coreBanner.message'),
        ctaLabel: t('addToCollectionPopover.coreBanner.ctaLabel'),
        ctaBiData: BiData({
            evid: 375,
            referral_info: ReferralType.PRIVATE_COLLECTIONS_POPUP
        })
    }), /*#__PURE__*/ React.createElement(CollectionsList, {
        collectibleDeviation: collectibleDeviation,
        isPrivateCollections: isPrivateCollections,
        filterValue: filterValue,
        allDisabled: reachedCollectLimit
    }));
    if (isMobile) {
        return /*#__PURE__*/ React.createElement(DSModal, {
            isOpen: true,
            heading: popupTitle,
            subheading: popupSubtitle,
            className: className,
            contentClassName: s['content-wrapper'],
            showHeaderBorder: true,
            showFooterBorder: true,
            footerRight: [
                footerPrimaryButton
            ],
            footerLeft: [
                newCollectionButton
            ],
            withScrollableContentWrapper: false,
            onRequestClose: onClose
        }, popupContent);
    }
    return /*#__PURE__*/ React.createElement(DSPopover, {
        heading: popupTitle,
        subheading: popupSubtitle,
        className: c(s['popover-root'], className),
        contentClassName: s['content-wrapper'],
        showFooterBorder: true,
        footerRight: footerPrimaryButton,
        footerLeft: newCollectionButton,
        onCloseClick: onClose,
        content: popupContent
    });
};
AddToCollectionPopup.displayName = 'AddToCollectionPopup';
export default AddToCollectionPopup;
